import React from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PaintingForm } from '../forms/PaintingForm';

export function PaintingFormModal({ 
  isOpen, 
  onClose, 
  painting,
  onSubmit,
  series,
  colors,
  awards,
  exhibitions,
  onSeriesUpdate,
  onAwardsUpdate
}) {
  const [localFormData, setLocalFormData] = React.useState(painting || {
    color_ids: [],
    award_ids: [],
    exhibition_ids: [],
    images: [],
    name: '',
    year: '',
    size: '',
    price: '',
    serie_id: '',
    description_de: '',
    description_en: '',
    main_image_idx: 0,
    sold: false,
    featured: false
  });
  
  const [deletedImageIds, setDeletedImageIds] = React.useState([]);

  // Update localFormData when painting prop changes
  React.useEffect(() => {
    if (painting) {
      setLocalFormData(painting);
    } else {
      setLocalFormData({
        color_ids: [],
        award_ids: [],
        exhibition_ids: [],
        images: [],
        name: '',
        year: '',
        size: '',
        price: '',
        serie_id: '',
        description_de: '',
        description_en: '',
        main_image_idx: 0,
        sold: false,
        featured: false
      });
    }
    // Reset deleted image IDs when modal opens/closes
    setDeletedImageIds([]);
  }, [painting, isOpen]);

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target;
    
    // Special handling for images to track deletions
    if (name === 'images' && Array.isArray(localFormData.images) && Array.isArray(value)) {
      // Check if an image was removed
      const removedImages = localFormData.images.filter(oldImg => 
        !value.some(newImg => 
          (oldImg.id && newImg.id === oldImg.id) || 
          (oldImg.file_path && newImg.file_path === oldImg.file_path)
        )
      );
      
      // Add removed image IDs to deletion list
      removedImages.forEach(img => {
        if (img.id && typeof img.id === 'number') {
          setDeletedImageIds(prev => [...prev, img.id]);
        }
      });
    }
    
    setLocalFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Create a new FormData or pass the localFormData
    const submitData = { 
      ...localFormData,
      delete_image_ids: deletedImageIds 
    };
    await onSubmit({ preventDefault: () => {}, target: {} }, submitData);
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-black dark:bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 text-left shadow-xl transition-all my-4 sm:my-8 w-full max-h-[95vh] sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl">
                <div className="absolute right-2 top-2 sm:right-4 sm:top-4 z-10">
                  <button
                    type="button"
                    className="rounded-lg p-2 bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 transition-all duration-200"
                    onClick={onClose}
                  >
                    <span className="sr-only">Schließen</span>
                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div className="bg-white dark:bg-zinc-800 flex flex-col h-full max-h-[95vh]">
                  {/* Header mit Titel */}
                  <div className="flex-shrink-0 px-4 sm:px-6 pt-5 pb-4 border-b border-gray-200 dark:border-zinc-700">
                    <Dialog.Title as="h3" className="text-xl sm:text-2xl font-semibold leading-6 text-gray-900 dark:text-white">
                      {painting ? 'Gemälde bearbeiten' : 'Neues Gemälde'}
                    </Dialog.Title>
                  </div>
                  
                  {/* Scrollbarer Inhalt */}
                  <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
                    <div className="text-left">
                      <PaintingForm
                        formData={localFormData}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        isEdit={!!painting}
                        series={series}
                        colors={colors}
                        awards={awards}
                        exhibitions={exhibitions}
                        onSeriesUpdate={onSeriesUpdate}
                        onAwardsUpdate={onAwardsUpdate}
                      />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}